import React from 'react';
import { ArrowUpRight, ArrowDownRight, PauseCircle, Crosshair, Gauge, ClipboardList } from 'lucide-react';

const fmt = (v) => {
    if (v === null || v === undefined) return '—';
    const n = Number(v);
    const digits = n >= 1000 ? 2 : n >= 1 ? 4 : 8;
    return `$${n.toLocaleString('en-US', { maximumFractionDigits: digits })}`;
};

export const dirMeta = (d) =>
    d === 'long'
        ? { label: 'LONG', word: 'YUKARI', Icon: ArrowUpRight, cls: 'text-emerald-300', box: 'border-emerald-500/40 bg-emerald-950/30', arrow: '↑' }
        : d === 'short'
          ? { label: 'SHORT', word: 'AŞAĞI', Icon: ArrowDownRight, cls: 'text-rose-300', box: 'border-rose-500/40 bg-rose-950/30', arrow: '↓' }
          : null;

const NeutralBox = ({ symbol }) => (
    <div data-testid={`radar-prep-neutral-${symbol}`} className="flex items-start gap-2 rounded-md border border-amber-400/40 bg-amber-950/20 px-2.5 py-2 text-[11px] text-amber-100 leading-snug">
        <PauseCircle className="h-4 w-4 shrink-0 text-amber-300" />
        <span>
            <b className="block text-amber-200">YÖN BELİRSİZ · Sistem BEKLE diyor</b>
            Teyitler birikiyor ama pozisyon analizi henüz LONG/SHORT vermiyor. İşlem açma.
        </span>
    </div>
);

const PlanRow = ({ k, v, sub, cls = 'text-slate-50' }) => (
    <div className="flex items-baseline justify-between gap-2 text-[11px]">
        <span className="text-slate-400">{k}</span>
        <span className={`font-mono text-right ${cls}`}>
            {v}
            {sub && <span className="text-slate-500 font-sans"> {sub}</span>}
        </span>
    </div>
);

export const OrderPlan = ({ plan, symbol }) => {
    if (!plan) return null;
    const isLong = plan.side === 'LONG';
    const conditional = plan.mode === 'conditional';
    return (
        <div data-testid={`radar-plan-${symbol}`} className="rounded-md border border-slate-600/80 bg-slate-950/60 px-2.5 py-2 space-y-1">
            <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wider text-sky-200 font-semibold">
                <ClipboardList className="h-3.5 w-3.5" />
                Koşullu emir planı · {plan.side}
            </div>
            <PlanRow k="Emir" v={plan.order_tr} cls="text-slate-100 font-sans text-[10px]" />
            {conditional ? (
                <PlanRow k="Giriş bölgesi" v={`${fmt(plan.entry_low)} – ${fmt(plan.entry_high)}`} />
            ) : (
                <PlanRow k="Giriş" v={fmt(plan.entry)} />
            )}
            <PlanRow k="Stop" v={`${fmt(plan.stop)}`} sub={`(${plan.stop_pct > 0 ? '+' : ''}${plan.stop_pct}%) · ${plan.stop_tr}`} cls="text-rose-300" />
            <PlanRow k="TP1" v={fmt(plan.tp1)} sub={`(${plan.tp1_pct > 0 ? '+' : ''}${plan.tp1_pct}%)`} cls={isLong ? 'text-emerald-300' : 'text-emerald-300'} />
            <PlanRow k="TP2" v={fmt(plan.tp2)} sub={`(${plan.tp2_pct > 0 ? '+' : ''}${plan.tp2_pct}%) · ${plan.tp_source === 'system' ? 'sistem S/R' : 'risk katı 2R/3.5R'}`} cls="text-emerald-300" />
            {plan.path_level && (
                <PlanRow k="Yolda seviye" v={fmt(plan.path_level)} sub={`(${plan.path_pct > 0 ? '+' : ''}${plan.path_pct}%) · kısmi kâr düşünebilirsin`} cls="text-slate-300" />
            )}
            <PlanRow k="R/R · Kaldıraç" v={`${plan.rr ?? '—'} · ${plan.leverage_label}`} cls={plan.rr >= 2 ? 'text-emerald-300' : 'text-amber-300'} />
            <div className="text-[10px] text-amber-200/80 leading-snug" data-testid={`radar-plan-confirm-${symbol}`}>{plan.confirm_tr}</div>
        </div>
    );
};

export default function RadarPrep({ rd, symbol }) {
    const m = dirMeta(rd.direction);
    if (!m) return <NeutralBox symbol={symbol} />;
    const lev = rd.leverage;
    const tr = rd.trigger;
    return (
        <div data-testid={`radar-prep-${symbol}`} className={`rounded-md border px-2.5 py-2 space-y-1.5 ${m.box}`}>
            <div className="flex items-center justify-between gap-2">
                <span data-testid={`radar-prep-direction-${symbol}`} className={`flex items-center gap-1 text-sm font-bold ${m.cls}`}>
                    <m.Icon className="h-4 w-4" />
                    {m.label}'a hazırlan
                    <span className="text-[10px] font-normal text-slate-400">({m.word})</span>
                </span>
                {lev && (
                    <span data-testid={`radar-prep-leverage-${symbol}`} title={lev.reason_tr} className="flex items-center gap-1 rounded-sm bg-slate-900/80 px-1.5 py-0.5 font-mono text-xs font-bold text-slate-50">
                        <Gauge className="h-3 w-3 text-sky-300" />
                        {lev.label}
                    </span>
                )}
            </div>
            {lev && <div className="text-[10px] text-slate-400" data-testid={`radar-prep-leverage-reason-${symbol}`}>Önerilen kaldıraç: {lev.reason_tr}</div>}
            {tr && (
                <div data-testid={`radar-prep-trigger-${symbol}`} className="flex items-start gap-1.5 text-[11px] text-slate-200 leading-snug">
                    <Crosshair className={`h-3.5 w-3.5 shrink-0 mt-px ${tr.triggered ? 'text-emerald-400' : 'text-sky-300'}`} />
                    <span>
                        <b className="text-slate-50">Tetik: {tr.side} {fmt(tr.price)}</b>
                        <span className="text-slate-400 font-mono"> ({tr.distance_pct > 0 ? '+' : ''}{tr.distance_pct}%)</span>
                        <span className="block text-[10px] text-slate-400">{tr.text_tr}</span>
                    </span>
                </div>
            )}
            <OrderPlan plan={rd.order_plan} symbol={symbol} />
        </div>
    );
}
